import { useState } from 'react';
import { KeyRound, ShieldCheck, AlertCircle, RefreshCw, Clock, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { axiosInstance } from '@edison/api-client-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface AccessToken {
  id: string;
  claimId: string;
  claimName: string | null;
  claimIncomeYear: string | null;
  label: string | null;
  role: string;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
}

function tokenStatus(t: AccessToken): 'active' | 'expired' | 'revoked' {
  if (t.revokedAt) return 'revoked';
  if (new Date(t.expiresAt) < new Date()) return 'expired';
  return 'active';
}

const ROLE_LABEL: Record<string, string> = {
  auditor: 'Auditor',
  advisor: 'Advisor',
  due_diligence: 'Due Diligence',
};

export function AccessTokensPage() {
  const queryClient = useQueryClient();
  const [revoking, setRevoking] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['access-tokens-all'],
    queryFn: async () => {
      const res = await axiosInstance.get('/api/access-tokens');
      return res.data.data.items as AccessToken[];
    },
  });

  async function handleRevoke(id: string) {
    setRevoking(id);
    try {
      await axiosInstance.post(`/api/access-tokens/${id}/revoke`);
      await queryClient.invalidateQueries({ queryKey: ['access-tokens-all'] });
    } finally {
      setRevoking(null);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Access tokens</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Scoped auditor and advisor portal access</p>
        </div>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 rounded-2xl border border-hairline bg-surface-2 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Access tokens</h1>
        </div>
        <div className="flex items-center gap-3 p-4 rounded-2xl border border-destructive/30 bg-destructive/5 text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="text-sm flex-1">Failed to load access tokens.</span>
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const tokens = data ?? [];
  const active = tokens.filter(t => tokenStatus(t) === 'active');
  const inactive = tokens.filter(t => tokenStatus(t) !== 'active');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Access tokens</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Scoped portal access for auditors and advisors. Tokens are issued from within a locked claim.
        </p>
      </div>

      {tokens.length === 0 && (
        <div className="text-center py-16 border border-dashed rounded-2xl">
          <KeyRound className="h-8 w-8 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No access tokens issued yet.</p>
          <p className="text-xs text-muted-foreground mt-1">Tokens are created from within a locked claim.</p>
        </div>
      )}

      {active.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Active</h2>
          {active.map(t => (
            <TokenRow key={t.id} token={t} revoking={revoking} onRevoke={handleRevoke} />
          ))}
        </section>
      )}

      {inactive.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Expired / Revoked</h2>
          {inactive.map(t => (
            <TokenRow key={t.id} token={t} revoking={revoking} onRevoke={handleRevoke} />
          ))}
        </section>
      )}
    </div>
  );
}

function TokenRow({
  token,
  revoking,
  onRevoke,
}: {
  token: AccessToken;
  revoking: string | null;
  onRevoke: (id: string) => void;
}) {
  const status = tokenStatus(token);

  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl border border-hairline bg-card">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 bg-surface-2">
        {status === 'active'
          ? <ShieldCheck className="h-4 w-4 text-positive-foreground" aria-hidden="true" />
          : <Ban className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        }
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-ink truncate">
            {token.label ?? ROLE_LABEL[token.role] ?? token.role}
          </span>
          <Badge variant="info" className="text-[11px] shrink-0">
            {ROLE_LABEL[token.role] ?? token.role}
          </Badge>
          {status === 'revoked' && <Badge variant="destructive" className="text-[11px] shrink-0">Revoked</Badge>}
          {status === 'expired' && <Badge variant="warning" className="text-[11px] shrink-0">Expired</Badge>}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-[11px] text-dim flex-wrap">
          {token.claimName && (
            <span>{token.claimName}{token.claimIncomeYear ? ` · ${token.claimIncomeYear}` : ''}</span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden="true" />
            {status === 'revoked' && token.revokedAt
              ? `Revoked ${new Date(token.revokedAt).toLocaleDateString()}`
              : `Expires ${new Date(token.expiresAt).toLocaleDateString()}`
            }
          </span>
        </div>
      </div>

      {status === 'active' && (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
          disabled={revoking === token.id}
          onClick={() => onRevoke(token.id)}
        >
          {revoking === token.id ? 'Revoking…' : 'Revoke'}
        </Button>
      )}
    </div>
  );
}
